<template>
  <div class="franchises-subaccounts-table">
    <DataTable
      :config="tableConfig"
      :data="accounts"
      :refreshButton="{ show: true }"
      @row-click="handleRowClick"
      @edit-click="handleEditClick"
      @button-click="handleButtonClick"
      @refresh="loadSubaccounts"
      ref="datatableRef"
    />

    <!-- Webhook Configuration Modal -->
    <modal
      elem-id="modal_franchises_subaccounts_webhook"
      :show="webhookDialogVisible"
      :title="t('Configurar Webhook')"
      size="small"
      :closeLabel="t('Fechar')"
      :saveLabel="webhookStatus?.configured ? t('Reconfigurar Webhook') : t('Configurar Webhook')"
      saveIcon="pi pi-cog"
      :saveFn="handleConfigureWebhook"
      :saveDisabledFn="() => configuringWebhook || webhookStatusLoading"
      @close="webhookDialogVisible = false"
      @dismiss="webhookDialogVisible = false"
    >
      <div v-if="selectedAccountForWebhook" class="webhook-config-content">
        <div class="account-info">
          <strong>{{ selectedAccountForWebhook.name }}</strong>
          <small>{{ selectedAccountForWebhook.email }}</small>
        </div>

        <Divider />

        <div v-if="webhookStatusLoading" class="loading-state">
          <spinner style="width: 30px; height: 30px" />
          <span>{{ t('Verificando status do webhook...') }}</span>
        </div>

        <div v-else-if="webhookStatus" class="webhook-status-details">
          <div class="status-row">
            <span class="label">{{ t('Status') }}:</span>
            <Tag
              :severity="webhookStatus.configured ? (webhookStatus.enabled ? (webhookStatus.interrupted ? 'danger' : 'success') : 'warning') : 'secondary'"
              :value="webhookStatus.configured ? (webhookStatus.enabled ? (webhookStatus.interrupted ? t('Interrompido') : t('Ativo')) : t('Desativado')) : t('Não configurado')"
            />
          </div>

          <div v-if="webhookStatus.configured" class="status-row">
            <span class="label">{{ t('URL') }}:</span>
            <code class="url-value">{{ webhookStatus.webhook_url }}</code>
          </div>

          <div v-if="webhookStatus.configured" class="status-row">
            <span class="label">{{ t('Token') }}:</span>
            <Tag
              :severity="webhookStatus.has_auth_token ? 'success' : 'warning'"
              :value="webhookStatus.has_auth_token ? t('Configurado') : t('Não configurado')"
            />
          </div>

          <div v-if="webhookStatus.configured && webhookStatus.events?.length" class="events-section">
            <span class="label">{{ t('Eventos inscritos') }} ({{ webhookStatus.events.length }}):</span>
            <div class="events-list">
              <Tag
                v-for="event in webhookStatus.events"
                :key="event"
                severity="info"
                :value="formatEventName(event)"
                class="event-tag"
              />
            </div>
          </div>

          <inline-message v-if="webhookStatus.interrupted" severity="error" :closable="false">
            {{ t('Webhook interrompido devido a muitas falhas. Reconfigure para reativar.') }}
          </inline-message>
        </div>
      </div>
    </modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../../eventBus';
import { log } from '../../../utils/DebugLogger';
import { useFranchisesSubaccounts } from '../../../composables/useFranchisesSubaccounts';
import { usePaymentProviders } from '../../../composables/usePaymentProviders';

type Id = number | string;

interface SubaccountRecord {
  id: Id;
  name?: string;
  email?: string;
  document?: string;
  status?: string;
  provider_code?: string;
  franchises?: Array<{ franquia?: string; name?: string }>;
}

interface WebhookStatus {
  configured?: boolean;
  enabled?: boolean;
  interrupted?: boolean;
  supports_webhook_config?: boolean;
  webhook_url?: string;
  has_auth_token?: boolean;
  events?: string[];
}

interface CellParams {
  data: SubaccountRecord & { _isFrozenFooter?: boolean };
  value?: unknown;
}

interface ButtonClickPayload {
  data: SubaccountRecord;
  action: string;
}

interface DataTableInstance {
  loadData?: () => void;
}

interface ProviderStatusConfig {
  label: string;
  severity?: string;
  icon?: string;
}

const { t } = useI18n();
const {
  list: listSubaccounts,
  sync: syncSubaccount,
  getWebhookStatus,
  configureWebhook
} = useFranchisesSubaccounts();
const { getProviderDisplayName, getStatusConfig } = usePaymentProviders();

const emit = defineEmits<{
  edit: [account: SubaccountRecord];
  config: [account: SubaccountRecord];
  fiscal: [account: SubaccountRecord];
}>();

// State
const accounts = ref<SubaccountRecord[]>([]);
const isLoading = ref<boolean>(false);
const syncingAccountId = ref<Id | null>(null);
const datatableRef = ref<DataTableInstance | null>(null);

// Webhook dialog state
const webhookDialogVisible = ref<boolean>(false);
const selectedAccountForWebhook = ref<SubaccountRecord | null>(null);
const webhookStatus = ref<WebhookStatus | null>(null);
const webhookStatusLoading = ref<boolean>(false);
const configuringWebhook = ref<boolean>(false);

// Table configuration for the custom DataTable wrapper
const tableConfig = computed(() => ({
  pagination: true,
  pageLength: 10,
  stripedRows: true,
  showGridlines: true,
  size: 'small',
  sortable: true,
  showFilters: true, // Enable filters
  columns: [
    {
      data: 'id',
      title: t('ID'),
      sortable: true,
      width: 80,
      renderType: 'clickable_id',
      filterType: 'number'
    },
    {
      data: 'name',
      title: t('Nome'),
      sortable: true,
      filterType: 'text',
      cellRenderer: ({ data }: CellParams) => {
        const providerName = getProviderDisplayName(data.provider_code);
        const div = document.createElement('div');
        div.className = 'account-name';
        div.innerHTML = `
          <span class="name-text">${data.name || ''}</span>
          <small class="provider-code">${providerName}</small>
        `;
        return div;
      }
    },
    {
      data: 'email',
      title: t('Email'),
      sortable: true,
      filterType: 'text'
    },
    {
      data: 'document',
      title: t('CPF/CNPJ'),
      sortable: true,
      filterType: 'text',
      cellRenderer: ({ data }: CellParams) => {
        return formatDocument(data.document);
      }
    },
    {
      data: 'status',
      title: t('Status'),
      sortable: true,
      filterType: 'text',
      cellRenderer: ({ data }: CellParams) => {
        const config = getStatusConfig(data.status) as ProviderStatusConfig;
        const span = document.createElement('span');
        span.className = `p-tag p-tag-${config.severity || 'info'}`;
        span.innerHTML = `${config.icon ? `<i class="${config.icon}"></i> ` : ''}${config.label}`;
        return span;
      }
    },
    {
      data: 'franchises',
      title: t('Franquias'),
      sortable: false,
      filterable: false,
      cellRenderer: ({ data }: CellParams) => {
        const franchises = data.franchises || [];
        if (franchises.length === 0) {
          return `<span class="no-franchises">${t('Nenhuma')}</span>`;
        }
        const tags = franchises.map(f => {
          const name = f.franquia || f.name || '-';
          return `<span class="p-tag p-tag-secondary franchise-tag">${name}</span>`;
        }).join(' ');
        return `<div class="franchise-tags">${tags}</div>`;
      }
    },
    {
      data: null,
      title: t('Webhook'),
      sortable: false,
      filterable: false,
      width: 100,
      renderType: 'clickable_button',
      buttonText: t('Config'),
      buttonIcon: 'pi pi-link',
      buttonClass: 'p-button-text p-button-rounded p-button-sm p-button-secondary',
      buttonAction: 'webhook'
    },
    {
      data: null,
      title: t('Sincronizar'),
      sortable: false,
      filterable: false,
      width: 80,
      renderType: 'clickable_button',
      buttonText: '',
      buttonIcon: 'pi pi-refresh',
      buttonClass: 'p-button-text p-button-rounded p-button-sm',
      buttonAction: 'sync'
    }
  ]
}));

// Methods
const loadSubaccounts = async (): Promise<void> => {
  log('FranchisesSubaccountsTable', 'loadSubaccounts', 'Loading subaccounts');
  isLoading.value = true;
  try {
    accounts.value = (await listSubaccounts()) as SubaccountRecord[];
    log('FranchisesSubaccountsTable', 'loadSubaccounts', 'Loaded subaccounts', accounts.value.length);
  } catch (err) {
    log('FranchisesSubaccountsTable', 'loadSubaccounts', 'Error', err);
  } finally {
    isLoading.value = false;
  }
};

const handleRowClick = (rowData: unknown): void => {
  const data = rowData as SubaccountRecord;
  log('FranchisesSubaccountsTable', 'handleRowClick', 'Row clicked', data.id);
};

const handleButtonClick = ({ data, action }: ButtonClickPayload): void => {
  log('FranchisesSubaccountsTable', 'handleButtonClick', 'Button clicked', { action, data });

  switch (action) {
    case 'config':
      handleConfig(data);
      break;
    case 'fiscal':
      handleFiscalConfig(data);
      break;
    case 'sync':
      handleSync(data);
      break;
    case 'webhook':
      handleWebhookDialog(data);
      break;
  }
};

const handleEditClick = (accountId: Id): void => {
  log('FranchisesSubaccountsTable', 'handleEditClick', 'Edit clicked for account ID', accountId);
  // The clickable_id renderType emits only the ID value (not full rowData)
  // Find the account from our loaded data
  const account = accounts.value.find(a => a.id === accountId);
  if (account) {
    emit('edit', account);
  }
};

const handleConfig = (account: SubaccountRecord): void => {
  log('FranchisesSubaccountsTable', 'handleConfig', 'Config clicked', account.id);
  emit('config', account);
};

const handleFiscalConfig = (account: SubaccountRecord): void => {
  log('FranchisesSubaccountsTable', 'handleFiscalConfig', 'Fiscal config clicked', account.id);
  emit('fiscal', account);
};

const handleSync = async (subaccount: SubaccountRecord): Promise<void> => {
  log('FranchisesSubaccountsTable', 'handleSync', 'Syncing subaccount', subaccount.id);
  syncingAccountId.value = subaccount.id;
  try {
    await syncSubaccount(Number(subaccount.id));
    await loadSubaccounts();
  } catch (err) {
    log('FranchisesSubaccountsTable', 'handleSync', 'Error', err);
  } finally {
    syncingAccountId.value = null;
  }
};

const hasInvoiceCapability = (account: SubaccountRecord): boolean => {
  // Check if the provider supports invoices
  const providersWithInvoices = ['asaas'];
  return providersWithInvoices.includes(account.provider_code || '');
};

const formatDocument = (doc?: string): string => {
  if (!doc) return '-';
  // Format CPF (11 digits) or CNPJ (14 digits)
  const cleaned = doc.replace(/\D/g, '');
  if (cleaned.length === 11) {
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  } else if (cleaned.length === 14) {
    return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return doc;
};

// Webhook methods
const handleWebhookDialog = async (account: SubaccountRecord): Promise<void> => {
  log('FranchisesSubaccountsTable', 'handleWebhookDialog', 'Opening webhook dialog', account.id);
  selectedAccountForWebhook.value = account;
  webhookStatus.value = null;
  webhookDialogVisible.value = true;

  // Load webhook status
  webhookStatusLoading.value = true;
  try {
    webhookStatus.value = (await getWebhookStatus(Number(account.id))) as WebhookStatus;
    log('FranchisesSubaccountsTable', 'handleWebhookDialog', 'Webhook status', webhookStatus.value);
  } catch (err) {
    log('FranchisesSubaccountsTable', 'handleWebhookDialog', 'Error fetching status', err);
    webhookStatus.value = { configured: false, supports_webhook_config: false };
  } finally {
    webhookStatusLoading.value = false;
  }
};

const handleConfigureWebhook = async (): Promise<void> => {
  if (!selectedAccountForWebhook.value) return;

  log('FranchisesSubaccountsTable', 'handleConfigureWebhook', 'Configuring webhook', selectedAccountForWebhook.value.id);
  configuringWebhook.value = true;

  try {
    await configureWebhook(Number(selectedAccountForWebhook.value.id));
    eventBus.emit('globalMessage.success', t('Webhook configurado com sucesso'));

    // Refresh status
    webhookStatus.value = (await getWebhookStatus(Number(selectedAccountForWebhook.value.id))) as WebhookStatus;
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    log('FranchisesSubaccountsTable', 'handleConfigureWebhook', 'Error', err);
    eventBus.emit('globalMessage.error', message || t('Erro ao configurar webhook'));
  } finally {
    configuringWebhook.value = false;
  }
};

const formatEventName = (event: string): string => {
  // Convert PAYMENT_RECEIVED to Payment Received
  return event
    .toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Event handlers for refresh
const handleRefresh = (): void => {
  log('FranchisesSubaccountsTable', 'handleRefresh', 'Refreshing table');
  loadSubaccounts();
};

// Lifecycle
onMounted(() => {
  log('FranchisesSubaccountsTable', 'onMounted', 'Component mounted');
  loadSubaccounts();

  // Listen for refresh events
  eventBus.on('franchisessubaccount.created', handleRefresh);
  eventBus.on('franchisessubaccount.updated', handleRefresh);
  eventBus.on('franchisessubaccount.refresh', handleRefresh);
});

onBeforeUnmount(() => {
  eventBus.off('franchisessubaccount.created', handleRefresh);
  eventBus.off('franchisessubaccount.updated', handleRefresh);
  eventBus.off('franchisessubaccount.refresh', handleRefresh);
});

// Expose refresh method
defineExpose({
  refresh: loadSubaccounts
});
</script>

<style scoped>
.franchises-subaccounts-table {
  background: var(--surface-card);
  border-radius: 8px;
  padding: 1rem;
}

:deep(.account-name) {
  display: flex;
  flex-direction: column;
}

:deep(.name-text) {
  font-weight: 500;
}

:deep(.provider-code) {
  color: var(--text-color-secondary);
  font-size: 0.75rem;
}

:deep(.franchise-tags) {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

:deep(.franchise-tag) {
  font-size: 0.75rem;
}

:deep(.no-franchises) {
  color: var(--text-color-secondary);
  font-style: italic;
}

/* Webhook modal styles */
.webhook-config-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.webhook-config-content .account-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.webhook-config-content .account-info strong {
  font-size: 1.1rem;
}

.webhook-config-content .account-info small {
  color: var(--text-color-secondary);
}

.webhook-config-content .loading-state {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  justify-content: center;
}

.webhook-status-details {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.webhook-status-details .status-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.webhook-status-details .status-row .label {
  font-weight: 600;
  min-width: 100px;
}

.webhook-status-details .url-value {
  font-size: 0.85rem;
  background: var(--surface-ground);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  word-break: break-all;
}

.webhook-status-details .events-section {
  margin-top: 0.5rem;
}

.webhook-status-details .events-section .label {
  font-weight: 600;
  display: block;
  margin-bottom: 0.5rem;
}

.webhook-status-details .events-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.webhook-status-details .event-tag {
  font-size: 0.75rem;
}
</style>
